/**
 * Front-end script for the gallery block.
 * Handles the lightbox overlay, the before/after view toggle,
 * and the slider navigation including swipe and keyboard support.
 */
import {__} from "@wordpress/i18n";

document.addEventListener("DOMContentLoaded", function () {
	// Track the current image index and the list of images in the active gallery
	let currentImageIndex = 0;
	let currentGalleryImages = [];
	let isRTL = document.documentElement.dir === 'rtl' || document.body.classList.contains('rtl');

	/**
	 * Creates and appends the lightbox overlay DOM structure to the document body.
	 * @returns {HTMLElement} The lightbox overlay element.
	 */
	function createLightbox() {
		const overlay = document.createElement('div');
		overlay.className = 'lightbox-overlay';
		overlay.setAttribute('role', 'dialog');
		overlay.setAttribute('aria-modal', 'true');
		overlay.setAttribute('aria-label', 'Image preview');

		overlay.innerHTML = `
			<span class="lightbox-close" aria-label="Close"><span class="dashicons dashicons-no-alt"></span></span>
			<button class="lightbox-prev" aria-label="Previous image"><span class="dashicons dashicons-arrow-left-alt2"></span></button>
			<button class="lightbox-next" aria-label="Next image"><span class="dashicons dashicons-arrow-right-alt2"></span></button>
			<img class="lightbox-image" src="" alt="" />
			<div class="lightbox-caption"></div>
		`;

		document.body.appendChild(overlay);
		return overlay;
	}

	const lightbox = createLightbox();
	const lightboxImg = lightbox.querySelector('.lightbox-image');
	const lightboxCaption = lightbox.querySelector('.lightbox-caption');
	const lightboxClose = lightbox.querySelector('.lightbox-close');
	const lightboxPrev = lightbox.querySelector('.lightbox-prev');
	const lightboxNext = lightbox.querySelector('.lightbox-next');

	/**
	 * Opens the lightbox and displays the image at the given index.
	 * @param {number}          index  - Index of the image to display.
	 * @param {HTMLElement[]}   images - Array of gallery image <figure> elements.
	 */
	function openLightbox(index, images) {
		currentImageIndex = index;
		currentGalleryImages = images;

		const figure = images[currentImageIndex];
		const img = figure.querySelector('img');
		const caption = figure.querySelector('figcaption')?.innerHTML || '';

		// Get orientation from parent gallery block
		const galleryBlock = figure.closest('.wp-block-superprof-gallery-block');
		const orientation = galleryBlock?.getAttribute('data-orientation');

		// Clean up any existing orientation-* classes
		lightbox.classList.remove('landscape', 'portrait', 'square');
		if (orientation) {
			lightbox.classList.add(orientation);
		}

		lightboxImg.src = img.src;
		lightboxImg.alt = img.alt;
		lightboxCaption.innerHTML = caption;
		if(caption === '') {
			lightboxCaption.classList.add('empty');
		} else {
			lightboxCaption.classList.remove('empty');
		}
		lightbox.classList.add('visible');
		document.body.classList.add('no-scroll');
		lightboxClose.focus();
	}

	/** Closes the lightbox and clears its content after the CSS transition. */
	function closeLightbox() {
		lightbox.classList.remove('visible', 'landscape', 'portrait', 'square');
		document.body.classList.remove('no-scroll');
		setTimeout(() => {
			lightboxImg.src = '';
			lightboxCaption.innerHTML = '';
			lightboxCaption.classList.remove('empty');
		}, 300);
	}

	/** Advances the lightbox to the next image (wraps around). */
	function showNext() {
		currentImageIndex = (currentImageIndex + 1) % currentGalleryImages.length;
		openLightbox(currentImageIndex, currentGalleryImages);
	}

	/** Goes back to the previous image in the lightbox (wraps around). */
	function showPrev() {
		currentImageIndex = (currentImageIndex - 1 + currentGalleryImages.length) % currentGalleryImages.length;
		openLightbox(currentImageIndex, currentGalleryImages);
	}

	// Attach handlers
	lightboxClose.addEventListener('click', closeLightbox);
	lightbox.addEventListener('click', e => {
		if (e.target === lightbox) closeLightbox();
	});
	lightboxNext.addEventListener('click', e => {
		e.stopPropagation();
		showNext();
	});
	lightboxPrev.addEventListener('click', e => {
		e.stopPropagation();
		showPrev();
	});
	document.addEventListener('keydown', e => {
		if (!lightbox.classList.contains('visible')) return;
		if (e.key === 'Escape') closeLightbox();
		else if (e.key === 'ArrowRight') showNext();
		else if (e.key === 'ArrowLeft') showPrev();
	});

	// Setup click handlers on each image
	document.querySelectorAll('.wp-block-superprof-gallery-block').forEach(gallery => {
		const view = gallery.getAttribute('data-view');
		const images = Array.from(gallery.querySelectorAll('.wp-block-superprof-gallery-image'));
		images.forEach((img, index) => {
			img.querySelector('img').addEventListener('click', () => {
				if ((view === 'slider' || view === 'before-after') && !img.classList.contains('active')) {
					return; // Do nothing if not active
				}

				openLightbox(index, images);
			});
		});
	});

	let touchStartX = 0;
	let touchEndX = 0;

	lightbox.addEventListener('touchstart', (e) => {
		touchStartX = e.changedTouches[0].screenX;
	}, false);

	lightbox.addEventListener('touchend', (e) => {
		touchEndX = e.changedTouches[0].screenX;
		handleSwipeGesture();
	}, false);

	function handleSwipeGesture() {
		const deltaX = touchEndX - touchStartX;
		const swipeThreshold = 50; // minimum swipe distance in px

		if (Math.abs(deltaX) > swipeThreshold) {
			if (deltaX > 0) {
				// Swiped right — show previous image
				showPrev();
			} else {
				// Swiped left — show next image
				showNext();
			}
		}
	}

	document.querySelectorAll('.wp-block-superprof-gallery-block').forEach(gallery => {
		if(gallery.getAttribute('data-view') === 'before-after') {
			// Dynamically creates Before/After buttons
			const controlsContainer = gallery.querySelector('.before-after-controls');
			if (controlsContainer) {
				controlsContainer.innerHTML = `
					<button class="before-btn">${__("Avant", "superprof-gutenberg-blocks")}</button>
					<button class="after-btn">${__("Après", "superprof-gutenberg-blocks")}</button>
				`;
			}

			const wrapper = gallery.querySelector('.gallery-wrapper');
			const beforeBtn = gallery.querySelector('.before-btn');
			const afterBtn = gallery.querySelector('.after-btn');
			const figures = wrapper.querySelectorAll('.wp-block-superprof-gallery-image');

			if (figures.length < 2) return;

			function showBefore() {
				// Slide first image in from left, second slides out right
				figures[0].classList.add('active');
				figures[0].classList.remove('slide-out-right', 'slide-out-left');

				figures[1].classList.remove('active');
				figures[1].classList.add('slide-out-right');

				beforeBtn.classList.add('active');
				afterBtn.classList.remove('active');
			}

			function showAfter() {
				// Slide second image in from right, first slides out left
				figures[1].classList.add('active');
				figures[1].classList.remove('slide-out-left', 'slide-out-right');

				figures[0].classList.remove('active');
				figures[0].classList.add('slide-out-left');

				beforeBtn.classList.remove('active');
				afterBtn.classList.add('active');
			}

			beforeBtn.addEventListener('click', showBefore);
			afterBtn.addEventListener('click', showAfter);

			// Initialize showing 'before' by default
			showBefore();

			// Swipe support for before-after
			addGallerySwipeSupport(gallery, showBefore, showAfter);
		}

		if(gallery.getAttribute('data-view') === 'slider')
		{
			if (!gallery) return;

			if (gallery.getAttribute('data-piano') === 'true') {
				const scoreDifficulty = gallery.querySelector('.score-difficulty');
				if (scoreDifficulty && scoreDifficulty.querySelectorAll('a').length === 0) {
					const levels = [
						{ href: '#all', text: __('Tout', 'superprof-gutenberg-blocks'), key: 'all' },
						{ href: '#beginner', text: __('Débutant', 'superprof-gutenberg-blocks'), key: 'beginner' },
						{ href: '#intermediate', text: __('Intermédiaire', 'superprof-gutenberg-blocks'), key: 'intermediate' },
						{ href: '#advanced', text: __('Avancé', 'superprof-gutenberg-blocks'), key: 'advanced' },
						{ href: '#confirmed', text: __('Confirmé', 'superprof-gutenberg-blocks'), key: 'confirmed' }
					];
					levels.forEach((level, id) => {
						const allItems = Array.from(gallery.querySelectorAll('.wp-block-superprof-gallery-image'));

						// Checks if there is at least one image matching this level
						const hasMatch = level.key === 'all' || allItems.some(item => item.getAttribute('data-difficulty') === level.key);

						if (!hasMatch) return; // Don't create the button if no match

						const link = document.createElement('a');
						link.href = level.href;
						link.textContent = level.text;
						link.style.display = 'block';
						if (id === 0) {
							link.classList.add('active');
						}

						link.addEventListener('click', (e) => {
							e.preventDefault();
							const allItems = Array.from(gallery.querySelectorAll('.wp-block-superprof-gallery-image'));
							active = 0; // Reset to first visible image
							let visibleCount = 0;

							gallery.querySelector('.score-difficulty a.active')?.classList.remove('active');
							link.classList.add('active');

							allItems.forEach(item => {
								const difficulty = item.getAttribute('data-difficulty');
								if (level.key === 'all' || difficulty === level.key) {
									item.style.display = '';
									item.style.width = '';
									visibleCount++;
								} else {
									item.style.display = 'none';
								}
							});

							// Update items and dots after filtering
							items = Array.from(gallery.querySelectorAll('.wp-block-superprof-gallery-image')).filter(item => item.style.display !== 'none');
							itemsLength = items.length - 1;

							// Reset the dots if needed
							dots = gallery.querySelectorAll('.dots li');
							dots.forEach(dot => dot.remove()); // cleans the old ones
							const dotsContainer = gallery.querySelector('.dots');
							items.forEach((_, index) => {
								const dot = document.createElement('li');
								if (index === (isRTL ? itemsLength : 0)) dot.classList.add('active');
								dotsContainer.appendChild(dot);
							});
							dots = gallery.querySelectorAll('.dots li'); // update reference

							waitForStableDimensions(reloadSlider);
						});
						scoreDifficulty.appendChild(link);
					});
				}
			}

			let list = gallery.querySelector('.slider-track');
			let items = gallery.querySelectorAll('.wp-block-superprof-gallery-image');
			let dots = gallery.querySelectorAll('.dots li');
			let prev = gallery.querySelector('.slider-nav .prev');
			let next = gallery.querySelector('.slider-nav .next');

			let itemsLength = items.length - 1;
			let active = isRTL ? itemsLength : 0;

			// Attach click listeners on initial dots
			dots.forEach((dot, index) => {
				dot.addEventListener('click', () => {
					active = index;
					reloadSlider();
				});
			});

			/**
			 * Recalculates and applies the slider layout:
			 * - Centers the active image in the track.
			 * - Updates the active dot indicator.
			 * - Adjusts the height of the track and the nav/dots vertical position.
			 * - Enables/disables prev and next buttons at the boundaries.
			 */
			function reloadSlider() {
				let galleryWidth = gallery.offsetWidth;
				const activeItem = items[active];
				const itemWidth = activeItem.getBoundingClientRect().width;
				const itemOffsetLeft = activeItem.offsetLeft;
				let centeredLeft;
				if (isRTL) {
					const trackWidth = list.scrollWidth;
					centeredLeft = (trackWidth - itemOffsetLeft - itemWidth) - (galleryWidth / 2) + (itemWidth / 2);
				} else {
					centeredLeft = itemOffsetLeft - (galleryWidth / 2) + (itemWidth / 2);
				}
				const track = gallery.querySelector('.slider-track');
				const nav = gallery.querySelector('.slider-nav');
				const img = activeItem.querySelector('img');
				const dotsContainer = gallery.querySelector('.dots');

				if (img && track) {
					const updatePositions = () => {
						const scoreHeight = gallery.querySelector('.score-difficulty')?.offsetHeight || 0;
						const imgHeight = img.offsetHeight;

						track.style.height = activeItem.offsetHeight + 'px';

						const isPiano = gallery.getAttribute('data-piano') === 'true';

						dotsContainer.style.top = isPiano
							? imgHeight + scoreHeight + 'px'
							: imgHeight + 'px';

						nav.style.top = isPiano
							? (imgHeight - 30) / 2 + scoreHeight + 'px'
							: (imgHeight + 30) / 2 + 'px';
					};

					if (img.complete) {
						updatePositions();
					} else {
						img.onload = updatePositions;
					}
				}

				list.style.left = isRTL ? centeredLeft + 'px' : -centeredLeft + 'px';

				let lastActiveDot = gallery.querySelector('.dots li.active');
				if (lastActiveDot) lastActiveDot.classList.remove('active');
				dots[active].classList.add('active');

				items.forEach((item, index) => {
					item.classList.toggle('active', index === active);
				});

				// Disable buttons if at start or end
				prev.disabled = isRTL ? active === itemsLength : active === 0;
				next.disabled = isRTL ? active === 0 : active === itemsLength;
			}

			// Delay layout check by a few animation frames
			/**
			 * Defers a callback until the active item's dimensions have been stable
			 * for 3 consecutive animation frames, avoiding layout jumps during transitions.
			 * @param {Function} callback - Function to call once dimensions are stable.
			 */
			function waitForStableDimensions(callback) {
				const element = items[active];
				let lastRect = element.getBoundingClientRect();
				let framesStable = 0;

				function check() {
					const newRect = element.getBoundingClientRect();
					if (
						Math.abs(newRect.width - lastRect.width) < 1 &&
						Math.abs(newRect.height - lastRect.height) < 1
					) {
						framesStable++;
					} else {
						framesStable = 0;
						lastRect = newRect;
					}

					if (framesStable >= 3) {
						callback(); // dimensions stable for 3 frames
					} else {
						requestAnimationFrame(check);
					}
				}

				requestAnimationFrame(check);
			}

			// ResizeObserver to detect when gallery size *actually* changes
			const resizeObserver = new ResizeObserver(() => {
				waitForStableDimensions(reloadSlider);
			});
			resizeObserver.observe(gallery);

			// handle manual window resize as backup
			window.addEventListener('resize', () => {
				waitForStableDimensions(reloadSlider);
			});

			// Initial setup
			reloadSlider();

			next.onclick = function () {
				if(isRTL) {
					if (active > 0)
					{
						active -= 1;
						reloadSlider();
					}
				} else {
					if (active < itemsLength) {
						active += 1;
						reloadSlider();
					}
				}

			};

			prev.onclick = function () {
				if(isRTL) {
					if (active < itemsLength)
					{
						active += 1;
						reloadSlider();
					}
				} else {
					if (active > 0) {
						active -= 1;
						reloadSlider();
					}
				}
			};

			// Save function on gallery so it's accessible if needed
			gallery._reloadSlider = reloadSlider;

			// Swipe support for slider
			addGallerySwipeSupport(gallery, prev.onclick, next.onclick);

		}
	});

	/**
	 * Attaches touch swipe event listeners to a gallery element.
	 * Calls the appropriate callback when the user swipes left or right.
	 * @param {HTMLElement} gallery       - The gallery DOM element to listen on.
	 * @param {Function}    onSwipeRight  - Called when the user swipes right (go back).
	 * @param {Function}    onSwipeLeft   - Called when the user swipes left (go forward).
	 */
	function addGallerySwipeSupport(gallery, onSwipeRight, onSwipeLeft) {
		let swipeStart = 0;
		let swipeEnd = 0;

		gallery.addEventListener('touchstart', (e) => {
			swipeStart = e.changedTouches[0].screenX;
		}, { passive: true });

		gallery.addEventListener('touchend', (e) => {
			swipeEnd = e.changedTouches[0].screenX;
			const dx = swipeEnd - swipeStart;
			const threshold = 50;
			if (Math.abs(dx) < threshold) return;

			if (dx > 0) {
				onSwipeRight?.(); // Swipe right
			} else {
				onSwipeLeft?.(); // Swipe left
			}
		}, { passive: true });
	}

});
